import React, {
	CSSProperties,
	FocusEventHandler,
	ForwardedRef,
	HTMLAttributes,
	MouseEventHandler,
	ReactNode,
	forwardRef,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';

import { mergeRefs } from '@shared/utils/react';

import styles from './index.module.less';

interface BaseInputProps
	extends Omit<
		HTMLAttributes<HTMLInputElement>,
		'type' | 'value' | 'className' | 'style' | 'disabled' | 'prefix'
	> {
	type?: string;
	prefix?: ReactNode;
	suffix?: ReactNode;
	disabled?: boolean;
	value: string;
	maxLength?: number;
	size?: 'large' | 'middle' | 'small' | 'mini' | 'nano';
	className?: string;
	inputClassName?: string; // input元素的className
	style?: CSSProperties;
	verifyFn?: (text: string) => boolean;
	verifyText?: string;
	placeholder?: string;
	innerInputRef?: ForwardedRef<HTMLInputElement>;
	errorClassName?: string;
}

function alwaysTrue() {
	return true;
}

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>((props, ref) => {
	const {
		prefix,
		suffix,
		value,
		type = 'text',
		disabled = false,
		size = 'middle',
		className = '',
		inputClassName,
		style = {},
		maxLength,
		verifyFn = alwaysTrue,
		verifyText = '',
		innerInputRef,
		errorClassName = '',
		...rest
	} = props;

	const isVerified = useMemo(() => {
		return verifyFn(value);
	}, [value, verifyFn]);

	const inputRef = useRef<HTMLInputElement>(null);

	const [isFocus, setIsFocus] = useState(false);

	const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
		setIsFocus(true);
		rest.onFocus?.(e);
	};

	const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
		setIsFocus(false);
		rest.onBlur?.(e);
	};

	const handleMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
		if (disabled) return;
		(e.target as HTMLElement).tagName !== 'INPUT' && e.preventDefault();
		if (document.activeElement !== inputRef.current) {
			inputRef.current?.focus();
		}
	};

	return (
		<React.Fragment>
			<div
				className={classNames({
					[styles.wrapper]: true,
					[styles.disable]: disabled,
					[styles.focus]: isFocus,
					[styles.error]: !isVerified,
					[styles[`wrapper_${size}`]]: true,
					[className]: !!className,
				})}
				style={style}
				onMouseDown={handleMouseDown}
				ref={ref}
			>
				{prefix && prefix}
				<input
					ref={innerInputRef ? mergeRefs(innerInputRef, inputRef) : inputRef}
					type={type}
					value={value}
					className={classNames(styles.input, inputClassName)}
					disabled={disabled}
					{...rest}
					maxLength={maxLength}
					onFocus={handleFocus}
					onBlur={handleBlur}
				/>
				{suffix && suffix}
				{!isVerified && (
					<div
						className={classNames({
							[styles.error_text]: true,
							[errorClassName]: !!errorClassName,
						})}
					>
						{verifyText}
					</div>
				)}
			</div>
		</React.Fragment>
	);
});
